import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Box,
  TextField,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import MapIcon from '@mui/icons-material/Map';
import ForestIcon from '@mui/icons-material/Forest';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SettingsIcon from '@mui/icons-material/Settings';

import ContactStep from '../Components/FormStepper/ContactStep';
import PropertyStep from '../Components/FormStepper/PropertyStep';
import EnquiryDetailsStep from '../Components/FormStepper/EnquiryDetailsStep';
import useEnquiryForm from '../CustomHooks/useEnquiryForm';
import TimberSalesForm from '../Components/FormStepper/TimberSalesForm';
import NotesTab from '../Components/Notes/NotesTab';

const QUICK_LINKS = [
  { label: 'Maps', href: 'https://www.google.com/maps', icon: MapIcon },
  { label: 'Forestry Viewer', href: 'https://flv.apps.services.agriculture.gov.ie/', icon: ForestIcon },
  { label: 'Land Direct', href: 'https://www.landdirect.ie/', icon: EventNoteIcon },
  {
    label: 'Procedure to Login and Retrieve Felling Licences',
    href: 'https://worrellharvestingltd.sharepoint.com/:w:/s/WorrellHarvestingLTD/EWHK6b0RR8ZJisjBI-BTQNkB9F3h9UL2NNDL6pOpxEHLTg?e=tD4oWb',
    icon: SettingsIcon,
  },
];

/**
 * One form, one flow: pick or create a contact, then fill in their
 * details. Under the hood this still fires two API calls (contact, then
 * enquiry) exactly as before — see useEnquiryForm / useContactsOverviewController.
 *
 * Replaces: EnquiryFormModal (old), ContactFormDialog, GeneralDetailsForm,
 * useContactForm.
 */

const ENQUIRY_TYPES = [
  { value: 'General', title: 'General Enquiry', desc: 'Contact details and follow-up' },
  { value: 'Timber Purchase', title: 'Timber Purchase', desc: 'Full enquiry including property & forest' },
  { value: 'Timber Sale', title: 'Timber Sales', desc: 'Separate template' },
];

export default function EnquiryFormModal({
  open,
  onClose,
  editingContact = null,
  handleEnquiryformSubmit,
  getEnquiryDetails,
  contacts = [],
  selectedTeam,
  currentStage,
  docsState = {},
  getContactDocs,
  uploadContactDocs,
  deleteContactDoc,
  downloadContactDoc,
  teams,
  stages,
  teamMembers,
  handleTimberSaleSubmit,
  getTimberSales,
  deleteTimberSaleRow,
  salesReferenceData,
  notesState = {},
  getContactNotes,
  addContactNote,
  currentUserId,
  getFollowUpStatus,
}) {
  const form = useEnquiryForm({
    open,
    contacts,
    selectedTeam,
    currentStage,
    editingContact,
    getEnquiryDetails,
    handleEnquiryformSubmit,
    getContactDocs,
    uploadContactDocs,
    docsState,
    teams,
    stages,
    teamMembers,
    handleTimberSaleSubmit,
    getTimberSales,
    deleteTimberSaleRow,
    salesReferenceData,
  });

  const { mode, pickerValue, handleContactPick, contactOptions, loadingContactDetails, hasExistingEnquiry, submitting, submit, enquiryType, setEnquiryType, isSales, selectedCrmId } = form;
  const noteContact = editingContact || pickerValue;
  const contactNotes = selectedCrmId ? notesState[selectedCrmId] : null;
  const followUpStatus = noteContact && noteContact.crmStageId !== 19
    ? getFollowUpStatus?.(noteContact)
    : null;
  const criticalFollowUp = followUpStatus === 'overdue' || followUpStatus === 'near'
    ? {
      status: followUpStatus,
      date: noteContact.localFollowUpDate || noteContact.followUpDate,
      stage: noteContact.crmStageName,
    }
    : null;

  useEffect(() => {
    if (open && mode === 'existing' && selectedCrmId && getContactNotes) {
      getContactNotes(selectedCrmId);
    }
  }, [open, mode, selectedCrmId, getContactNotes]);

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open} aria-labelledby="enquiry-form-modal" PaperProps={{
      style: { overflowY: 'hidden' },   // ← plain `style`, NOT `sx`
    }}>
      <DialogTitle sx={{ m: 0, pt: 2.5, pb: 1, px: 3 }} id="enquiry-form-modal">
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {editingContact ? 'Edit Timber Enquiry' : 'Timber Enquiry'}
        </Typography>
      </DialogTitle>
      <IconButton onClick={onClose} sx={theme => ({ position: 'absolute', right: 12, top: 12, color: theme.palette.grey[500] })}>
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ backgroundColor: '#fdfcfb' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Quick links */}
          <Box
            sx={{
              backgroundColor: '#f2f0ec',
              borderRadius: '10px',
              padding: '6px 14px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '4px',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, mr: 1, color: '#6b6558' }}>
              LINKS
            </Typography>
            {QUICK_LINKS.map(({ label, href, icon: Icon }) => (
              <Tooltip title={label} key={label}>
                <IconButton href={href} target="_blank" rel="noopener" size="small">
                  <Icon fontSize="small" sx={{ color: '#7a5c3e' }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          {!enquiryType ? (
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1.5 }}>
                What kind of enquiry is this?
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                {ENQUIRY_TYPES.map(t => (
                  <Box
                    key={t.value}
                    onClick={() => setEnquiryType(t.value)}
                    sx={{
                      cursor: 'pointer', p: 2.5, borderRadius: '10px', textAlign: 'center',
                      border: '1px solid #d0c9bf', backgroundColor: '#fff',
                      '&:hover': { borderColor: '#7a5c3e', backgroundColor: '#faf8f5' },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#4a3f2f' }}>{t.title}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#8a8375', mt: 0.5 }}>{t.desc}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#6b6558' }}>Enquiry type:</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                  {ENQUIRY_TYPES.find(t => t.value === enquiryType)?.title}
                </Typography>
                {!editingContact && (
                  <Button size="small" onClick={() => setEnquiryType('')}>Change</Button>
                )}
              </Box>

              {isSales ? (
                <TimberSalesForm form={form} />
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.7fr) minmax(300px, 1fr)' },
                    alignItems: 'start',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    {editingContact ? (
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                          Editing enquiry for
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                          {editingContact.FullName ||
                            `${editingContact.crmContactFirst || ''} ${editingContact.crmContactLast || ''}`.trim()}
                        </Typography>
                        {loadingContactDetails && (
                          <Typography sx={{ fontSize: '0.78rem', color: '#8a8375', mt: 0.5 }}>
                            Loading their details…
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>Who is this enquiry from?</Typography>
                        <Autocomplete
                          size="small"
                          options={contactOptions}
                          value={pickerValue}
                          onChange={(e, v) => handleContactPick(v)}
                          isOptionEqualToValue={(o, v) => o.crmId === v?.crmId}
                          getOptionLabel={o =>
                            o.__new ? 'Create New Contact' : o.FullName || `${o.crmContactFirst || ''} ${o.crmContactLast || ''}`.trim()
                          }
                          filterOptions={(opts, s) => {
                            const input = s.inputValue.toLowerCase();
                            if (!input) return opts;
                            return opts.filter(
                              o =>
                                o.__new ||
                                (o.FullName || '').toLowerCase().includes(input) ||
                                (o.crmContactPhone || '').toLowerCase().includes(input) ||
                                (o.crmContactEmail || '').toLowerCase().includes(input),
                            );
                          }}
                          renderOption={(props, option) =>
                            option.__new ? (
                              <li {...props} key="new-contact" style={{ fontWeight: 700 }}>
                                + Create New Contact
                              </li>
                            ) : (
                              <li {...props} key={option.crmId}>
                                {option.FullName}
                                {option.crmContactPhone ? ` — ${option.crmContactPhone}` : ''}
                              </li>
                            )
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder="Search existing contacts, or create a new one"
                              InputProps={{ ...params.InputProps, startAdornment: <PersonSearchIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.5 }} /> }}
                            />
                          )}
                        />
                        {mode === 'existing' && (
                          <Typography sx={{ fontSize: '0.78rem', color: '#8a8375', mt: 0.5 }}>
                            {loadingContactDetails
                              ? 'Loading their details…'
                              : hasExistingEnquiry
                                ? 'This contact already has a Timber Enquiry on file — saving will update it.'
                                : 'This will be their first Timber Enquiry.'}
                          </Typography>
                        )}
                        {mode === 'new' && <Typography sx={{ fontSize: '0.78rem', color: '#8a8375', mt: 0.5 }}>Creating a brand new contact.</Typography>}
                      </Box>
                    )}

                    {!mode && !editingContact ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography color="text.secondary">Select or create a contact above to continue.</Typography>
                      </Box>
                    ) : (
                      <>
                        <ContactStep form={form} />
                        {form.showPropertyStep && <PropertyStep form={form} />}
                        <EnquiryDetailsStep form={form} deleteContactDoc={deleteContactDoc} downloadContactDoc={downloadContactDoc} />
                      </>
                    )}
                  </Box>

                  <Box sx={{ border: '1px solid #ece7df', borderRadius: '10px', p: 2.5, backgroundColor: '#fff', minWidth: 0 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 0.5 }}>View / add notes</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#8a8375', mb: 2 }}>
                      Keep a running history of conversations and follow-ups.
                    </Typography>
                    <NotesTab
                      notes={contactNotes?.items || []}
                      loading={contactNotes?.loading || false}
                      onAddNote={(text, mentionedUsers) => addContactNote?.(noteContact, text, mentionedUsers)}
                      currentUserId={currentUserId}
                      criticalFollowUp={criticalFollowUp}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

        </Box>
      </DialogContent>

      {enquiryType && (isSales || mode) && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button onClick={submit} variant="contained" color="primary" disabled={submitting}>
            {submitting ? 'Saving…' : isSales ? 'Save Timber Sale' : 'Save Enquiry'}
          </Button>
        </DialogActions>
      )}
    </Dialog >
  );
}